import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import {
  MutationCache,
  QueryClient,
  QueryClientProvider,
} from '@tanstack/react-query';
import { toast } from 'sonner';
import './index.css';
import App from './App';
import { Toaster } from '@/components/ui/sonner';

/**
 * Global mutation error handler — Sonner toast per Phase 4 follow-on (item 1).
 *
 * Per the 2026 TanStack Query + shadcn pattern: configure a single
 * MutationCache.onError on the client. Each mutation opts in to a custom
 * message via `meta: { errorMessage: "..." }`, and out via
 * `meta: { skipToast: true }` when it has custom error UI of its own.
 *
 * Generic fallback message when meta is absent — better than silent failure.
 *
 * Source: https://spin.atomicobject.com/toast-notifications-tanstack-query/
 */
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5_000,
      refetchOnWindowFocus: true,
      retry: 1,
    },
  },
  mutationCache: new MutationCache({
    onError: (error, _variables, _context, mutation) => {
      const meta = mutation.meta as
        | { errorMessage?: string; skipToast?: boolean }
        | undefined;
      if (meta?.skipToast) return;
      const message =
        meta?.errorMessage ??
        (error instanceof Error
          ? error.message
          : 'Something went wrong — try again.');
      toast.error(message);
    },
  }),
});

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <App />
        <Toaster richColors position="top-right" />
      </BrowserRouter>
    </QueryClientProvider>
  </StrictMode>,
);
